import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { DateTimePicker } from 'react-widgets';
import moment from 'moment';
import { LabelItem } from '../Forms/LabelItem';
import { InputTheme } from '../Forms/InputStyled';
import theme from '../../assets/css/theme';
import { UncontrolledTooltip } from 'reactstrap';

const LabelItemStyled = styled(LabelItem)`
  width: fit-content;
`;

const Wrapper = styled.div`
  margin-bottom: 10px;

  .rw-widget-picker {
    border-radius: 20px;
    border: 1px solid transparent;

    &:not(:last-child) {
      border-right: none;
    }

    &::placeholder {
      font-weight: normal;
      color: ${theme.commonColors.main};
      opacity: 1;
    }

    &:focus {
      padding-left: 10px;
    }

    ${InputTheme.main};
  }

  .rw-select {
    border-left: none;
    padding-right: 4px;

    &:hover {
      background: #ffffff;
    }

    .rw-btn {
      color: ${theme.commonColors.second};
    }
  }

  .tooltip-icon {
    color: ${theme.commonColors.second};
    margin-left: 5px;
  }
`;

interface AnniversaryDatePickerProps {
  date: string;
  defaultDate?: string;
  callback: (value: string) => void;
  placeholder?: string;
  label?: string;
  minDate?: Date;
  maxDate?: Date;
  format?: string;
  id?: string;
  withTooltip?: boolean;
  tooltipText?: string;
}

export const AnniversaryDatePicker: React.FC<AnniversaryDatePickerProps> = ({
  date,
  defaultDate = '',
  callback,
  placeholder = '',
  label,
  minDate,
  maxDate,
  format = 'DD/MM',
  id,
  withTooltip = false,
  tooltipText = ''
}) => {
  // Initialize to valid date or today
  const [internalDate, setInternalDate] = useState<Date>(() => {
    const init = date || defaultDate;

    return init && moment(init).isValid() ? moment(init).toDate() : new Date();
  });

  const lastValidRef = useRef<Date>(internalDate);

  useEffect(() => {
    if (date && moment(date).isValid()) {
      const d = moment(date).toDate();
      setInternalDate(d);
      lastValidRef.current = d;
    }
  }, [date]);

  const [pickerKey, setPickerKey] = useState(0);

  const handleChange = (d: Date | null) => {
    if (!d) {
      // restore previous valid date by forcing remount
      setInternalDate(new Date(lastValidRef.current.getTime()));
      callback(lastValidRef.current.toISOString());
      setPickerKey((prev) => prev + 1);

      return;
    }
    // preserve year from lastValidRef
    const year = lastValidRef.current.getFullYear();
    const updated = new Date(year, d.getMonth(), d.getDate());
    setInternalDate(updated);
    lastValidRef.current = updated;
    callback(updated.toISOString());
  };

  const pickerRef = useRef<HTMLDivElement | null>(null);

  const handleBlur = () => {
    if (!pickerRef.current) return;

    const input = pickerRef.current.querySelector('input') as HTMLInputElement | null;

    const raw = input.value.trim();
    const valid = moment(raw, [format, moment.ISO_8601], true).isValid();

    if (!raw || !valid) {
      // revert to defaultDate if valid, else lastValid
      const restored =
        defaultDate && moment(defaultDate).isValid()
          ? moment(defaultDate).toDate()
          : lastValidRef.current;
      setInternalDate(restored);
      callback(restored.toISOString());
    }
  };

  const tooltipID = `${id}_tooltip`;

  return (
    <Wrapper ref={pickerRef}>
      {label && (
        <LabelItemStyled isSmall isBold id={tooltipID}>
          {label}
          {withTooltip && <i className="fa fa-question-circle tooltip-icon" />}
        </LabelItemStyled>
      )}
      {withTooltip && id && (
        <UncontrolledTooltip target={tooltipID} placement="right">
          {tooltipText}
        </UncontrolledTooltip>
      )}
      <DateTimePicker
        key={pickerKey}
        time={false}
        value={internalDate}
        placeholder={placeholder}
        onChange={handleChange}
        onBlur={handleBlur}
        min={minDate}
        max={maxDate}
        format={format}
        editFormat={format}
      />
    </Wrapper>
  );
};

export default AnniversaryDatePicker;
